import React, { useState } from 'react';
import { Search, Calendar, Clock, ArrowLeft, Sparkles } from 'lucide-react';
import blogPosts, { getCategories, getFeaturedPost } from '../data/realStagingStories';

const NavLink = ({ to, className, children, ...props }) => (
  <a href={to} className={className} {...props}>{children}</a>
);

const FlowBlog = () => {
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');

  const categories = getCategories();
  const featuredPost = getFeaturedPost();

  const filteredPosts = blogPosts.filter(post => {
    const matchesCategory = selectedCategory === 'All' || post.category === selectedCategory;
    const matchesSearch = post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         post.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const gridPosts = filteredPosts.filter(post => post.id !== featuredPost?.id);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 px-6 py-4 bg-slate-950/90 backdrop-blur-md border-b border-slate-800">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <NavLink to="/flow" className="flex items-center gap-2 text-white hover:text-sky-400 transition-colors">
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium">Back to Flow</span>
          </NavLink>
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-sky-400" />
            <span className="text-sky-400 font-semibold">Flow Blog</span>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-16 px-6 bg-gradient-to-b from-sky-900/20 to-transparent">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center px-4 py-2 rounded-full bg-sky-500/20 border border-sky-500/40 mb-6">
            <span className="text-sky-400 font-semibold text-sm tracking-wider uppercase">Flow AI Knowledge Center</span>
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
            Flow <span className="text-sky-400">Blog</span>
          </h1>
          <p className="text-slate-400 text-lg md:text-xl max-w-2xl mx-auto">
            Expert guides on virtual staging, social media marketing, lead generation, and growing your real estate business with AI.
          </p>
        </div>
      </section>

      {/* Blog Content */}
      <section className="py-12 px-6">
        <div className="max-w-6xl mx-auto">
          {/* Filters */}
          <div className="bg-slate-900 rounded-2xl p-6 mb-8 border border-slate-800">
            <h2 className="text-2xl font-bold text-center mb-6">Latest Articles</h2>

            <div className="flex flex-wrap items-center justify-center gap-4 border-t border-slate-700 pt-6">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search articles..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 pr-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-sky-500 w-48"
                />
              </div>

              {/* Categories */}
              <div className="flex flex-wrap gap-2">
                {categories.slice(0, 8).map((category) => (
                  <button
                    key={category}
                    onClick={() => setSelectedCategory(category)}
                    className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                      selectedCategory === category
                        ? 'text-sky-400 bg-sky-500/10 border border-sky-500/40'
                        : 'text-slate-400 hover:text-white border border-transparent'
                    }`}
                  >
                    {category}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Featured Post */}
          {selectedCategory === 'All' && !searchQuery && featuredPost && (
            <NavLink to={`/flow/blog/${featuredPost.slug}`} className="block mb-8">
              <div className="bg-white rounded-2xl overflow-hidden hover:shadow-xl transition-shadow flex flex-col md:flex-row">
                <div className="md:w-1/2">
                  <img
                    src={featuredPost.featuredImage}
                    alt={featuredPost.imageAlt}
                    className="w-full h-64 md:h-full object-cover"
                  />
                </div>
                <div className="md:w-1/2 p-8 flex flex-col justify-center">
                  <span className="text-sky-600 text-sm uppercase tracking-wider mb-2">Featured</span>
                  <h3 className="text-2xl md:text-3xl font-bold text-slate-900 mb-3 hover:text-sky-600 transition-colors">
                    {featuredPost.title}
                  </h3>
                  <span className="text-slate-500 text-sm uppercase tracking-wider mb-3">
                    {featuredPost.category}
                  </span>
                  <p className="text-slate-600 mb-4 line-clamp-2">
                    {featuredPost.excerpt}
                  </p>
                  <div className="flex items-center gap-4 text-slate-500 text-sm">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      {formatDate(featuredPost.date)}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {featuredPost.readTime} min read
                    </span>
                  </div>
                </div>
              </div>
            </NavLink>
          )}

          {/* Posts Grid */}
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {gridPosts.slice(0, 30).map((post) => (
              <NavLink
                key={post.id}
                to={`/flow/blog/${post.slug}`}
                className="bg-slate-900 rounded-xl overflow-hidden border border-slate-800 hover:border-sky-500/50 transition-all hover:-translate-y-1"
              >
                <img
                  src={post.featuredImage}
                  alt={post.imageAlt}
                  className="w-full h-48 object-cover"
                />
                <div className="p-6">
                  <span className="text-sky-400 text-xs uppercase tracking-wider">
                    {post.category}
                  </span>
                  <h3 className="text-lg font-semibold text-white mt-2 mb-2 line-clamp-2">
                    {post.title}
                  </h3>
                  <p className="text-slate-400 text-sm line-clamp-2 mb-4">
                    {post.excerpt}
                  </p>
                  <div className="flex items-center gap-4 text-slate-500 text-xs">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {formatDate(post.date)}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {post.readTime} min
                    </span>
                  </div>
                </div>
              </NavLink>
            ))}
          </div>

          {/* Load More */}
          {gridPosts.length > 30 && (
            <div className="text-center mt-8">
              <p className="text-slate-400">Showing 30 of {gridPosts.length} articles</p>
            </div>
          )}

          {/* No Results */}
          {filteredPosts.length === 0 && (
            <div className="text-center py-16">
              <p className="text-slate-400 text-lg">No articles found matching your criteria.</p>
              <button
                onClick={() => {
                  setSelectedCategory('All');
                  setSearchQuery('');
                }}
                className="mt-4 text-sky-400 hover:underline"
              >
                Clear filters
              </button>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-6 bg-gradient-to-r from-sky-900/20 to-violet-900/20">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to Transform Your Listings?</h2>
          <p className="text-slate-400 mb-8">
            Try Flow's AI-powered virtual staging and create stunning before/after content in seconds.
          </p>
          <NavLink
            to="/flow"
            className="inline-flex items-center gap-2 px-8 py-4 bg-sky-600 hover:bg-sky-500 text-white font-semibold rounded-xl transition-colors"
          >
            <Sparkles className="w-5 h-5" />
            Try Flow Free
          </NavLink>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-slate-800">
        <div className="max-w-6xl mx-auto text-center text-slate-500 text-sm">
          <p>&copy; 2026 Flow AI. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default FlowBlog;
